import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  IconButton, Autocomplete, TextField, Typography, Divider, Alert
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, AccountBalance as AccountIcon, Store as StoreIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import axiosInstance from '../../services/axiosInstance';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { DailySalesAccountConfiguration } from '../../types/dailySalesAccountConfiguration.types';

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
}

interface Concept {
  conceptId: number;
  conceptCode: string;
  conceptName: string;
  conceptNameAr: string;
  branchId: number;
  branchName: string;
  branchNameAr: string;
}

interface DialogProps {
  open: boolean;
  configuration: DailySalesAccountConfiguration | null;
  onClose: (refresh?: boolean) => void;
}

const DailySalesAccountConfigurationDialog: React.FC<DialogProps> = ({ open, configuration, onClose }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [formData, setFormData] = useState({
    configurationId: 0,
    conceptId: 0,
    cashAccountId: null as number | null,
    knetBankAccountId: null as number | null,
    cardBankAccountId: null as number | null,
    salesAccountId: null as number | null
  });
  const [loading, setLoading] = useState(false);

  const t = {
    title: configuration ? (isRTL ? 'تعديل إعدادات الحسابات' : 'Edit Account Configuration') : (isRTL ? 'إضافة إعدادات حسابات' : 'Add Account Configuration'),
    concept: isRTL ? 'المفهوم (Concept)' : 'Concept',
    selectConcept: isRTL ? 'اختر المفهوم' : 'Select Concept',
    cashAccount: isRTL ? 'حساب الصندوق (اختياري)' : 'Cash Account (Optional)',
    selectCashAccount: isRTL ? 'اختر حساب الصندوق' : 'Select Cash Account',
    knetBankAccount: isRTL ? 'حساب بنك الكي نت (اختياري)' : 'KNET Bank Account (Optional)',
    selectKnetAccount: isRTL ? 'اختر حساب بنك الكي نت' : 'Select KNET Bank Account',
    cardBankAccount: isRTL ? 'حساب بنك البطاقات (اختياري)' : 'Card Bank Account (Optional)',
    selectCardAccount: isRTL ? 'اختر حساب بنك البطاقات' : 'Select Card Bank Account',
    salesAccount: isRTL ? 'حساب المبيعات (اختياري)' : 'Sales Account (Optional)',
    selectSalesAccount: isRTL ? 'اختر حساب المبيعات' : 'Select Sales Account',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully',
    requiredConcept: isRTL ? 'المفهوم مطلوب' : 'Concept is required',
    accountsSection: isRTL ? 'ربط الحسابات' : 'Account Mappings',
    infoMessage: isRTL ? 'جميع الحسابات اختيارية - يمكنك إضافتها لاحقاً عند الحاجة' : 'All accounts are optional - you can add them later as needed'
  };

  useEffect(() => {
    if (open) {
      loadAccounts();
      loadConcepts();
      if (configuration) {
        setFormData({
          configurationId: configuration.configurationId,
          conceptId: configuration.conceptId,
          cashAccountId: configuration.cashAccountId || null,
          knetBankAccountId: configuration.knetBankAccountId || null,
          cardBankAccountId: configuration.cardBankAccountId || null,
          salesAccountId: configuration.salesAccountId || null
        });
      } else {
        resetForm();
      }
    }
  }, [open, configuration]);

  const loadAccounts = async () => {
    try {
      const data = await chartOfAccountsService.getAll();
      const finalAccounts = data.filter((acc: any) => acc.isFinalAccount && acc.isActive);
      setAccounts(finalAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadConcepts = async () => {
    try {
      const response = await axiosInstance.get('/Concepts');
      setConcepts(response.data);
    } catch (error) {
      console.error('Failed to load concepts:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      configurationId: 0,
      conceptId: 0,
      cashAccountId: null,
      knetBankAccountId: null,
      cardBankAccountId: null,
      salesAccountId: null
    });
  };

  const handleSave = async () => {
    if (!formData.conceptId) {
      toast.error(t.requiredConcept);
      return;
    }
    try {
      setLoading(true);
      if (configuration) {
        await axiosInstance.put(`/DailySalesAccountConfiguration/${configuration.configurationId}`, formData);
      } else {
        await axiosInstance.post('/DailySalesAccountConfiguration', formData);
      }
      toast.success(t.saveSuccess);
      onClose(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : 'Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  const selectedConcept = concepts.find(c => c.conceptId === formData.conceptId);
  const selectedCashAccount = accounts.find(a => a.accountId === formData.cashAccountId);
  const selectedKnetAccount = accounts.find(a => a.accountId === formData.knetBankAccountId);
  const selectedCardAccount = accounts.find(a => a.accountId === formData.cardBankAccountId);
  const selectedSalesAccount = accounts.find(a => a.accountId === formData.salesAccountId);

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#388E3C', color: 'white', fontWeight: 'bold' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountIcon />
          {t.title}
        </Box>
        <IconButton onClick={() => onClose(false)} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>{t.infoMessage}</Alert>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <StoreIcon sx={{ color: '#388E3C' }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#388E3C' }}>{t.concept}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Autocomplete
              options={concepts}
              getOptionLabel={(option) => `${option.conceptCode} - ${isRTL ? option.conceptNameAr : option.conceptName} (${isRTL ? option.branchNameAr : option.branchName})`}
              value={selectedConcept || null}
              onChange={(_, newValue) => setFormData({ ...formData, conceptId: newValue?.conceptId || 0 })}
              renderInput={(params) => <TextField {...params} label={t.selectConcept} required />}
              disabled={!!configuration}
              fullWidth
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountIcon sx={{ color: '#388E3C' }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#388E3C' }}>{t.accountsSection}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gap: 2 }}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                value={selectedCashAccount || null}
                onChange={(_, newValue) => setFormData({ ...formData, cashAccountId: newValue?.accountId || null })}
                renderInput={(params) => <TextField {...params} label={t.cashAccount} />}
                fullWidth
              />

              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                value={selectedKnetAccount || null}
                onChange={(_, newValue) => setFormData({ ...formData, knetBankAccountId: newValue?.accountId || null })}
                renderInput={(params) => <TextField {...params} label={t.knetBankAccount} />}
                fullWidth
              />

              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                value={selectedCardAccount || null}
                onChange={(_, newValue) => setFormData({ ...formData, cardBankAccountId: newValue?.accountId || null })}
                renderInput={(params) => <TextField {...params} label={t.cardBankAccount} />}
                fullWidth
              />

              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                value={selectedSalesAccount || null}
                onChange={(_, newValue) => setFormData({ ...formData, salesAccountId: newValue?.accountId || null })}
                renderInput={(params) => <TextField {...params} label={t.salesAccount} />}
                fullWidth
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={loading} sx={{ bgcolor: '#388E3C', '&:hover': { bgcolor: '#2E7D32' } }}>
          {loading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailySalesAccountConfigurationDialog;